/** @jsxImportSource @emotion/react */

import type { CSSObject } from "@emotion/react";
import { vars } from "@imwebme/clay-token";
import type { ChangeEventHandler, PropsWithChildren } from "react";
import type { LabelProps } from "../..";
import { Clay, Flex, Label, Typography } from "../..";
import { useSelectPortal, useSelectState, useSelectToggle } from "../Select.provider";
import { SelectTags } from "./Select.tags";
import { multiSelectCSS } from "./SelectTheme.css";

export type SelectInputProps = {
  label?: string;
  labelProps?: Omit<LabelProps<"label">, "text">;
  value?: string;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  placeholder?: string;
  isDisabled?: boolean;
  sx?: CSSObject;
};

export const SelectInputWrapper = ({
  label,
  labelProps,
  children,
  isDisabled,
  isTagInput,
  sx,
}: PropsWithChildren<SelectInputProps> & { isTagInput?: boolean }) => {
  const { isOpen } = useSelectState();
  const toggle = useSelectToggle();
  const setPortalRf = useSelectPortal();

  return (
    <Flex.Column ref={setPortalRf} gap={vars.spacing[15]} sx={sx}>
      {label && <Label text={label} {...labelProps} />}

      <Flex
        as="button"
        alignItems="center"
        position="relative"
        css={multiSelectCSS}
        native={{
          onClick: () => (isDisabled ? null : toggle()),
          type: "button",
          disabled: isDisabled,
        }}
        overflow="hidden"
        cursor={isDisabled ? "not-allowed" : "pointer"}
        color={vars.semantic.color[isDisabled ? "textDisabled" : "text"]}
      >
        {children}

        <Flex position="absolute" height="100%" right={0}>
          {isTagInput && (
            <Clay
              width="8px"
              background="var(--semantic-gradient-dark-fade-left, linear-gradient(270deg, #FFF 0%, rgba(255, 255, 255, 0.95) 25%, rgba(255, 255, 255, 0.85) 41.02%, rgba(255, 255, 255, 0.78) 50.13%, rgba(255, 255, 255, 0.65) 61.75%, rgba(255, 255, 255, 0.45) 73.87%, rgba(255, 255, 255, 0.25) 86.25%, rgba(255, 255, 255, 0.00) 100%))"
            />
          )}

          <Flex alignItems="center" backgroundColor="inherit" paddingRight={vars.spacing[3]}>
            {/* ChevronDown */}
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              css={{
                transform: `rotate(${isOpen ? 180 : 0}deg)`,
              }}
            >
              <path
                d="M6 9L12 15L18 9"
                stroke={vars.semantic.color[isDisabled ? "iconDisabled" : "icon"]}
                strokeWidth="1.2"
                vectorEffect="non-scaling-stroke"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Flex>
        </Flex>
      </Flex>
    </Flex.Column>
  );
};

export const SelectInput = ({
  value,
  placeholder,
  label,
  labelProps,
  onChange,
  isDisabled,
  sx,
}: SelectInputProps) => {
  return (
    <SelectInputWrapper label={label} labelProps={labelProps} isDisabled={isDisabled} sx={sx}>
      <input
        readOnly
        value={value}
        placeholder={placeholder}
        disabled={isDisabled}
        onChange={onChange}
        css={{
          width: "100%",
          height: "100%",
          outline: "none",
          cursor: isDisabled ? "not-allowed" : "pointer",
          backgroundColor: "unset",
          ...vars.semantic.typography["label-medium"],
        }}
      />
    </SelectInputWrapper>
  );
};

export type SelectMultiInputProps = {
  values?: string[];
  setValues?: (values: string[]) => void;
} & Omit<SelectInputProps, "value" | "onChange">;

export const SelectMultiInput = ({
  values,
  placeholder,
  setValues,
  ...restProps
}: SelectMultiInputProps) => {
  return (
    <SelectInputWrapper {...restProps}>
      {values && values.length > 0 ? (
        <SelectTags tags={values} setValues={setValues} />
      ) : (
        <Typography colorToken="textSub">{placeholder}</Typography>
      )}
    </SelectInputWrapper>
  );
};
